import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, Maximize, Minimize, Pause, Play, RotateCcw, Volume1, Volume2, VolumeX } from 'lucide-react';

interface VideoPlayerProps {
  src?: string;
  /** Sizing and placement of the player; it fills its box and letterboxes the picture. */
  className?: string;
  fit?: 'contain' | 'cover';
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  /** What is playing, for screen readers. */
  label?: string;
  /** Told the video's length once the browser knows it. */
  onDuration?: (seconds: number) => void;
}

const SPEEDS = [1, 1.25, 1.5, 2, 0.5];
const HIDE_AFTER_MS = 2200;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** 0:07, 2:31 or 1:02:31. A running time counts whole seconds; a length rounds to the nearest. */
export const formatClock = (seconds: number, round: 'down' | 'nearest' = 'down'): string => {
  const total = Number.isFinite(seconds) && seconds > 0 ? (round === 'nearest' ? Math.round(seconds) : Math.floor(seconds)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`;
};

/**
 * The app's video player, in place of the browser's own controls: an ink panel, with the
 * controls on a bar of dark glass over the picture. They are neutral (ink and cream), so the
 * player sits in a node of any colour. Controls fade out while the video plays and come back
 * on hover or a key.
 * Keys while it has focus: Space or K plays and pauses, arrows skip 5 seconds, M mutes,
 * F goes full screen. Small players drop the speed and volume controls.
 */
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src, className = '', fit = 'contain', autoPlay = false, muted = false, loop = false, preload = 'metadata', label = 'Video', onDuration,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(muted);
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  // Whether the pointer or a key was just used: the controls stay up for a moment after it.
  const [awake, setAwake] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [width, setWidth] = useState(0);
  const hideTimer = useRef<number | undefined>(undefined);
  const resumeAfterScrub = useRef(false);
  const findingDuration = useRef(false);

  const knownDuration = Number.isFinite(duration) && duration > 0;
  const onDurationRef = useRef(onDuration);
  onDurationRef.current = onDuration;
  useEffect(() => { if (knownDuration) onDurationRef.current?.(duration); }, [knownDuration, duration]);
  const compact = width > 0 && width < 340;
  const tiny = width > 0 && width < 230;
  const bigButton = !failed && (!started || ended);
  // Before the first play the big button is enough; the bar comes with the pointer
  const showControls = (!playing && !bigButton) || awake || scrubbing;

  // A new video starts over
  useEffect(() => {
    setPlaying(false); setStarted(false); setEnded(false); setWaiting(false); setFailed(false);
    setTime(0); setDuration(0); setBuffered(0);
  }, [src]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!rootRef.current && document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Smooth progress while playing; timeupdate alone moves in jumps
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      if (video) setTime(video.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  /** Show the controls for a moment after the pointer moves or a key is pressed. */
  const poke = useCallback(() => {
    setAwake(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setAwake(false), HIDE_AFTER_MS);
  }, []);

  const readBuffered = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    for (let i = 0; i < video.buffered.length; i++) {
      if (video.buffered.start(i) <= video.currentTime + 0.5 && video.currentTime <= video.buffered.end(i)) {
        setBuffered(video.buffered.end(i) / video.duration);
        return;
      }
    }
    setBuffered(0);
  };

  const toggle = () => {
    const video = videoRef.current;
    if (!video || failed) return;
    if (video.paused || video.ended) {
      if (video.ended) video.currentTime = 0;
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const seekTo = (seconds: number) => {
    const video = videoRef.current;
    if (!video || !knownDuration) return;
    video.currentTime = clamp(seconds, 0, duration);
    setTime(video.currentTime);
    setEnded(false);
  };

  const setLevel = (level: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = clamp(level, 0, 1);
    video.muted = level <= 0;
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      if (video.volume === 0) video.volume = 0.6;
      video.muted = false;
    } else {
      video.muted = true;
    }
  };

  const cycleSpeed = () => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void rootRef.current?.requestFullscreen?.().catch(() => {});
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const onButton = !!(e.target as HTMLElement).closest('button');
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if ((key === ' ' && !onButton) || key === 'k') toggle();
    else if (key === 'ArrowLeft') seekTo(time - 5);
    else if (key === 'ArrowRight') seekTo(time + 5);
    else if (key === 'm') toggleMute();
    else if (key === 'f') toggleFullscreen();
    else return;
    // The canvas pans on arrows and has its own shortcuts: keep these keys here
    e.preventDefault();
    e.stopPropagation();
    poke();
  };

  const level = isMuted ? 0 : volume;
  const VolumeIcon = level === 0 ? VolumeX : level < 0.5 ? Volume1 : Volume2;

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      role="group"
      aria-label={label}
      className={`relative overflow-hidden bg-ink select-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lavender ${fullscreen && playing && !awake ? 'cursor-none' : ''} ${className}`}
      onPointerMove={poke}
      onPointerLeave={() => { if (!scrubbing) setAwake(false); }}
      onFocus={poke}
      onKeyDown={onKeyDown}
      data-video-player
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        playsInline
        preload={preload}
        className={`block w-full h-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`}
        onClick={toggle}
        onDoubleClick={toggleFullscreen}
        onPlay={() => { setPlaying(true); setStarted(true); setEnded(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setEnded(true); }}
        onWaiting={() => setWaiting(true)}
        onPlaying={() => setWaiting(false)}
        onCanPlay={() => setWaiting(false)}
        onError={() => setFailed(true)}
        onTimeUpdate={(e) => { setTime(e.currentTarget.currentTime); readBuffered(); }}
        onProgress={readBuffered}
        onVolumeChange={(e) => { setVolume(e.currentTarget.volume); setIsMuted(e.currentTarget.muted); }}
        onRateChange={(e) => setSpeed(e.currentTarget.playbackRate)}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          // Recorded WebM files often leave their length out: seeking far ahead makes the browser work it out
          if (video.duration === Infinity) {
            findingDuration.current = true;
            video.currentTime = Number.MAX_SAFE_INTEGER;
          }
          setDuration(video.duration);
        }}
        onDurationChange={(e) => {
          const video = e.currentTarget;
          setDuration(video.duration);
          if (findingDuration.current && Number.isFinite(video.duration)) {
            findingDuration.current = false;
            video.currentTime = 0;
          }
        }}
      />

      {bigButton && (
        <button
          type="button"
          onClick={toggle}
          aria-label={ended ? 'Play again' : 'Play'}
          // A little above the middle, clear of the control bar: centred by its margins in a
          // box that reaches 36px over the top, which lifts it by half of that.
          style={{ top: -36 }}
          className={`absolute inset-0 m-auto ns-icon-btn ns-icon-btn-dark focus-visible:outline-lavender ${tiny ? '' : 'ns-icon-btn-lg'}`}
          data-player-big-play
        >
          {ended ? <RotateCcw /> : <Play className="ml-0.5" fill="currentColor" />}
        </button>
      )}

      {waiting && playing && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full ns-glass-dark pointer-events-none">
          <Loader2 size={20} className="animate-spin" />
        </span>
      )}

      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
          <AlertCircle size={20} className="text-danger" aria-hidden="true" />
          <p className="text-xs text-cream/80">This video could not be played.</p>
        </div>
      )}

      {!failed && (
        <div
          className={`absolute ns-glass-dark transition-opacity duration-200 ${tiny ? 'inset-x-1 bottom-1 rounded-xl px-1.5 py-0.5' : 'inset-x-2 bottom-2 rounded-2xl px-2 py-1'} ${showControls ? 'opacity-100' : 'node-reveal opacity-0 pointer-events-none'}`}
          data-player-controls
        >
          <Slider
            value={knownDuration ? time / duration : 0}
            buffered={buffered}
            label="Seek"
            valueText={`${formatClock(time)} of ${formatClock(duration, 'nearest')}`}
            disabled={!knownDuration}
            step={5 / Math.max(duration, 1)}
            tooltip={knownDuration ? (fraction) => formatClock(fraction * duration) : undefined}
            onStart={() => {
              const video = videoRef.current;
              resumeAfterScrub.current = !!video && !video.paused;
              video?.pause();
              setScrubbing(true);
            }}
            onChange={(fraction) => seekTo(fraction * duration)}
            onEnd={() => {
              setScrubbing(false);
              if (resumeAfterScrub.current) void videoRef.current?.play().catch(() => {});
            }}
            dataName="seek"
          />
          <div className="flex items-center gap-1 text-cream">
            <ControlButton label={playing ? 'Pause (K)' : 'Play (K)'} onClick={toggle} small={tiny}>
              {playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" className="ml-0.5" />}
            </ControlButton>
            <span className={`ml-1 font-medium tabular-nums whitespace-nowrap ${tiny ? 'text-2xs' : 'text-xs'}`} data-player-time>
              {ended ? formatClock(duration, 'nearest') : formatClock(time)}
              {!tiny && <span className="text-cream/60"> / {knownDuration ? formatClock(duration, 'nearest') : '-:--'}</span>}
            </span>
            <span className="flex-1" />
            {!compact && (
              <button
                type="button"
                onClick={cycleSpeed}
                title="Playback speed"
                aria-label={`Playback speed ${speed}x`}
                // A pill with text: the round buttons' dark fill, at their height
                className="ns-btn ns-btn-xs ns-btn-flat h-8 min-w-[40px] px-2 py-0 bg-ink/55 text-cream tabular-nums hover:bg-ink/80 focus-visible:outline-lavender"
                data-player-speed
              >
                {speed}x
              </button>
            )}
            <div className="flex items-center group/volume">
              <ControlButton label={level === 0 ? 'Unmute (M)' : 'Mute (M)'} onClick={toggleMute} small={tiny}>
                <VolumeIcon />
              </ControlButton>
              {!compact && (
                <div className="w-0 overflow-hidden transition-[width] duration-200 group-hover/volume:w-[72px] group-focus-within/volume:w-[72px]">
                  <div className="w-[64px] ml-1">
                    <Slider value={level} label="Volume" valueText={`${Math.round(level * 100)}%`} step={0.1} onChange={setLevel} dataName="volume" />
                  </div>
                </div>
              )}
            </div>
            <ControlButton label={fullscreen ? 'Exit full screen (F)' : 'Full screen (F)'} onClick={toggleFullscreen} small={tiny}>
              {fullscreen ? <Minimize /> : <Maximize />}
            </ControlButton>
          </div>
        </div>
      )}
    </div>
  );
};

const ControlButton: React.FC<{ label: string; onClick: () => void; small?: boolean; children: React.ReactNode }> = ({ label, onClick, small, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label.replace(/ \(.\)$/, '')}
    title={label}
    className={`ns-icon-btn ns-icon-btn-dark focus-visible:outline-lavender ${small ? 'ns-icon-btn-sm' : ''}`}
  >
    {children}
  </button>
);

/** A thin track, cream on dark: click or drag anywhere on it, or use the arrow keys. */
const Slider: React.FC<{
  value: number;
  buffered?: number;
  label: string;
  valueText: string;
  disabled?: boolean;
  step: number;
  tooltip?: (fraction: number) => string;
  onStart?: () => void;
  onChange: (fraction: number) => void;
  onEnd?: () => void;
  dataName: string;
}> = ({ value, buffered = 0, label, valueText, disabled = false, step, tooltip, onStart, onChange, onEnd, dataName }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [hover, setHover] = useState<number | null>(null);
  const [held, setHeld] = useState(false);

  // The track's on-screen box already includes the canvas zoom
  const fractionAt = (clientX: number) => {
    const box = trackRef.current?.getBoundingClientRect();
    return box && box.width > 0 ? clamp((clientX - box.left) / box.width, 0, 1) : 0;
  };
  const finish = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    setHeld(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    onEnd?.();
  };
  const shown = clamp(value, 0, 1) * 100;

  return (
    <div
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={valueText}
      aria-disabled={disabled}
      className={`relative flex items-center h-4 touch-none outline-none group/slider ${disabled ? 'opacity-40' : 'cursor-pointer'}`}
      onPointerDown={(e) => {
        if (disabled || e.button !== 0) return;
        // No text selection or focus jump while dragging
        e.preventDefault();
        e.currentTarget.focus({ preventScroll: true });
        e.currentTarget.setPointerCapture(e.pointerId);
        dragging.current = true;
        setHeld(true);
        onStart?.();
        onChange(fractionAt(e.clientX));
      }}
      onPointerMove={(e) => {
        if (disabled) return;
        const fraction = fractionAt(e.clientX);
        setHover(fraction);
        if (dragging.current) onChange(fraction);
      }}
      onPointerUp={finish}
      onPointerCancel={finish}
      onPointerLeave={() => setHover(null)}
      onKeyDown={(e) => {
        if (disabled) return;
        const next = e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? value - step
          : e.key === 'ArrowRight' || e.key === 'ArrowUp' ? value + step
          : e.key === 'Home' ? 0 : e.key === 'End' ? 1 : null;
        if (next === null) return;
        e.preventDefault();
        e.stopPropagation();
        onChange(clamp(next, 0, 1));
      }}
      data-player-slider={dataName}
    >
      <div ref={trackRef} className={`relative w-full rounded-full bg-cream/25 transition-[height] duration-150 ${held ? 'h-1.5' : 'h-1 group-hover/slider:h-1.5'}`}>
        {buffered > 0 && <div className="absolute inset-y-0 left-0 rounded-full bg-cream/30" style={{ width: `${clamp(buffered, 0, 1) * 100}%` }} />}
        <div className="absolute inset-y-0 left-0 rounded-full bg-cream" style={{ width: `${shown}%` }} />
        <div
          className={`absolute top-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-hair transition-transform duration-150 ${held ? 'scale-100' : 'scale-0 group-hover/slider:scale-100 group-focus-visible/slider:scale-100'}`}
          style={{ left: `${shown}%` }}
        />
        {tooltip && hover !== null && (
          <div
            className="absolute bottom-3 -translate-x-1/2 px-1.5 py-0.5 rounded-sm bg-cream text-ink text-2xs font-semibold tabular-nums whitespace-nowrap pointer-events-none shadow-hair"
            // Kept inside the player near either end
            style={{ left: `clamp(22px, ${hover * 100}%, calc(100% - 22px))` }}
          >
            {tooltip(hover)}
          </div>
        )}
      </div>
    </div>
  );
};

/** The player's thin slider, for other dark media controls (the editor's track levels). */
export const MediaSlider = Slider;

export default VideoPlayer;
